import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AutoRedemptionRule } from '@rd/types/auto-redemption-rule.type';
import {
  AutoRedemptionSetting,
  AutoRedemptionSettingStrategy,
  PeriodAutoRedemptionSettingStrategyOptions,
} from '@rd/types/auto-redemption-setting.type';
import { HttpError } from '@rd/types/http-error.type';
import { toObject } from '@rd/utils/array.utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CreateCashBackAutoRedemptionSetting,
  CreateDigitalGiftCardAutoRedemptionSetting,
  CreatePhysicalGiftCardAutoRedemptionSetting,
  CreatePointsTransferAutoRedemptionSetting,
} from '../../services/auto-redemption-setting-api/auto-redemption-setting-api.service';
import {
  CreateAutoRedemptionSetting,
  DeleteAutoRedemptionSetting,
  LoadAutoRedemptionSettings,
  ResetAutoRedemptionSettingState,
  SubmitAutoRedemptionSettingForm,
  UpdateAutoRedemptionSetting,
} from '../../store/actions/auto-redemption-settings.actions';
import { getAutoRedemptionSettingsFeatureState } from '../../store/reducers';
@Injectable({
  providedIn: 'root',
})
export class AutoRedemptionSettingFacade {
  constructor(private store: Store) {}

  autoRedemptionFormData$: Observable<{
    [id: string]:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting;
  }> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) => autoRedemptionSettingsState.formData,
      ),
    );

  autoRedemptionRules$: Observable<AutoRedemptionRule[]> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.autoRedemptionRules,
      ),
    );

  autoRedemptionRulesMap$: Observable<{
    [productSku: string]: AutoRedemptionRule;
  }> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map((autoRedemptionSettingsState) =>
        toObject(autoRedemptionSettingsState.autoRedemptionRules, 'productSku'),
      ),
    );

  canModifyAutoRedemption$: Observable<boolean> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map((autoRedemptionSettingState) => {
        const autoRedemptionSetting =
          autoRedemptionSettingState.autoRedemptionSettings
            .autoRedemptionSetting;
        const autoRedemptionRules =
          autoRedemptionSettingState.autoRedemptionRules;
        if (autoRedemptionSetting === null) {
          return false;
        }
        if (autoRedemptionRules.length === 0) {
          return true;
        }
        const correspondingRule = autoRedemptionRules.find(
          (rule) => rule.productSku === autoRedemptionSetting.productSku,
        );
        if (!correspondingRule) {
          return true;
        }

        if (!correspondingRule.enabled) {
          return false;
        }

        return correspondingRule.strategies.length > 1;
      }),
    );

  autoRedemptionSetting$: Observable<AutoRedemptionSetting | null> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.autoRedemptionSettings
            .autoRedemptionSetting,
      ),
    );

  getAutoRedemptionSettingsLoading$: Observable<boolean> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.autoRedemptionSettings.loading,
      ),
    );

  getAutoRedemptionSettingsError$: Observable<HttpError | null> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.autoRedemptionSettings.error,
      ),
    );

  deleteAutoRedemptionSettingSuccess$: Observable<boolean> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.deleteAutoRedemptionSettings.success,
      ),
    );

  createAutoRedemptionSettingLoading$: Observable<boolean> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.createAutoRedemptionSettings.loading,
      ),
    );

  createAutoRedemptionSettingSuccess$: Observable<boolean> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.createAutoRedemptionSettings.success,
      ),
    );

  createAutoRedemptionSettingError$: Observable<HttpError | null> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.createAutoRedemptionSettings.error,
      ),
    );

  updateAutoRedemptionSettingSuccess$: Observable<boolean> = this.store
    .select(getAutoRedemptionSettingsFeatureState)
    .pipe(
      map(
        (autoRedemptionSettingsState) =>
          autoRedemptionSettingsState.updateAutoRedemptionSettings.success,
      ),
    );

  getAutoRedemptionSettings(): void {
    this.store.dispatch(new LoadAutoRedemptionSettings());
  }

  // Need to copy over
  createAutoRedemptionSettings(
    data:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting,
    isEverydaySpend = false,
  ): void {
    this.store.dispatch(new CreateAutoRedemptionSetting(data, isEverydaySpend));
  }

  submitAutoRedemptionSettings(
    data:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting,
    isEverydaySpend = false,
  ): void {
    this.store.dispatch(
      new SubmitAutoRedemptionSettingForm(data, isEverydaySpend),
    );
  }

  deleteAutoRedemptionSetting(id: string, loyaltyProgramId?: string): void {
    this.store.dispatch(
      new DeleteAutoRedemptionSetting({ id, loyaltyProgramId }),
    );
  }

  updateAutoRedemption(
    id: string,
    pointsRequired: number,
    options: {
      strategy: AutoRedemptionSettingStrategy;
      strategyOptions: PeriodAutoRedemptionSettingStrategyOptions | {};
    },
  ): void {
    this.store.dispatch(
      new UpdateAutoRedemptionSetting({
        id,
        pointsRequired,
        strategy: options.strategy,
        strategyOptions: options.strategyOptions,
      }),
    );
  }

  resetAutoRedemptionSettingState(): void {
    this.store.dispatch(new ResetAutoRedemptionSettingState());
  }
}
